// src/components/BookCover/CreateFromExistingDialog.jsx
// Dialog for creating a new cover version by copying an existing one.
// Mirrors NewVersionDialog but adds a "Copy From" dropdown that auto-populates
// cover type and design criteria from the selected source version — unless the
// user has already filled those fields manually.

import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Typography, Box, Avatar,
  ToggleButtonGroup, ToggleButton, IconButton, Chip,
  FormControl, InputLabel, Select, MenuItem, CircularProgress,
  Divider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import BrushIcon from '@mui/icons-material/Brush';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CheckIcon from '@mui/icons-material/Check';
import { API_BASE } from '../../utils/api';
import VersionFrontThumbnail from './VersionFrontThumbnail';

const COVER_TYPES = [
  { id: 'softcover', label: 'Softcover (Paperback)' },
  { id: 'softcover_flaps', label: 'Softcover with Flaps' },
  { id: 'hardcover', label: 'Hardcover' },
  { id: 'hardcover_jacket', label: 'Hardcover with Dust Jacket' },
  { id: 'ebook', label: 'Ebook' },
];

export default function CreateFromExistingDialog({
  open,
  onClose,
  onConfirm,
  nextVersionNumber,
  versions = [],
}) {
  const [name, setName] = useState('');
  const [sourceVersionId, setSourceVersionId] = useState('');
  const [coverType, setCoverType] = useState('');
  const [criteria, setCriteria] = useState('');
  const [collaborators, setCollaborators] = useState([]);

  // Track whether the user has manually edited these fields — if they have,
  // we won't overwrite their input when they change the source version.
  const coverTypeTouched = useRef(false);
  const criteriaTouched = useRef(false);

  // Add-collaborators sub-dialog state (mirrors CollaboratorBar's add dialog UX)
  const [addOpen, setAddOpen] = useState(false);
  const [addSearchQuery, setAddSearchQuery] = useState('');
  const [addSearchResults, setAddSearchResults] = useState([]);
  const [addSearching, setAddSearching] = useState(false);
  const [addPendingUsers, setAddPendingUsers] = useState([]); // multi-select before commit
  const [addRole, setAddRole] = useState('design');

  const [creating, setCreating] = useState(false);

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      setName(`Version ${nextVersionNumber || ''}`);
      setSourceVersionId('');
      setCoverType('');
      setCriteria('');
      setCollaborators([]);
      coverTypeTouched.current = false;
      criteriaTouched.current = false;
    }
  }, [open, nextVersionNumber]);

  // Reset sub-dialog state every time it opens
  useEffect(() => {
    if (addOpen) {
      setAddSearchQuery('');
      setAddSearchResults([]);
      setAddPendingUsers([]);
      setAddRole('design');
    }
  }, [addOpen]);

  // When a source version is picked, auto-populate cover type / criteria
  // unless the user has already edited them.
  const handleSourceChange = (e) => {
    const id = e.target.value;
    setSourceVersionId(id);
    const src = versions.find(v => v.id === id);
    if (!src) return;
    if (!coverTypeTouched.current && src.coverType) {
      setCoverType(src.coverType);
    }
    if (!criteriaTouched.current && src.criteria) {
      setCriteria(src.criteria);
    }
  };

  // Debounced user search inside the sub-dialog. Excludes already-added
  // collaborators, the current user, and anyone already pending in this batch.
  useEffect(() => {
    if (!addOpen) return undefined;
    if (!addSearchQuery.trim()) {
      setAddSearchResults([]);
      return undefined;
    }
    const timeout = setTimeout(async () => {
      setAddSearching(true);
      try {
        const token = localStorage.getItem('rv_access_token');
        const res = await fetch(`${API_BASE}/api/users/search?q=${encodeURIComponent(addSearchQuery)}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          const existingIds = new Set([
            ...collaborators.map(c => c.userId),
            ...addPendingUsers.map(u => u._id || u.id),
          ]);
          const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
          const currentUserId = currentUser.id || currentUser._id;
          const filtered = (data.users || data || [])
            .filter(u => !existingIds.has(u._id || u.id))
            .filter(u => (u._id || u.id) !== currentUserId);
          setAddSearchResults(filtered);
        }
      } catch { /* ignore */ }
      finally { setAddSearching(false); }
    }, 300);
    return () => clearTimeout(timeout);
  }, [addSearchQuery, collaborators, addPendingUsers, addOpen]);

  const isPending = (u) => addPendingUsers.some(p => (p._id || p.id) === (u._id || u.id));

  const handleTogglePending = (user) => {
    const uid = user._id || user.id;
    setAddPendingUsers(prev =>
      prev.some(p => (p._id || p.id) === uid)
        ? prev.filter(p => (p._id || p.id) !== uid)
        : [...prev, user]
    );
    setAddSearchQuery('');
    setAddSearchResults([]);
  };

  const handleAddCommit = () => {
    if (addPendingUsers.length === 0) {
      setAddOpen(false);
      return;
    }
    setCollaborators(prev => [
      ...prev,
      ...addPendingUsers.map(u => ({
        userId: u._id || u.id,
        username: u.username,
        role: addRole,
      })),
    ]);
    setAddOpen(false);
  };

  const handleRemoveCollaborator = (userId) => {
    setCollaborators(prev => prev.filter(c => c.userId !== userId));
  };

  const handleCreate = async () => {
    setCreating(true);
    try {
      await onConfirm({
        name: name.trim() || undefined,
        sourceVersionId: sourceVersionId || undefined,
        collaborators: collaborators.map(c => ({ userId: c.userId, role: c.role })),
        criteria: criteria.trim() || undefined,
        coverType: coverType || undefined,
      });
      onClose();
    } catch {
      // Error handled by parent
    } finally {
      setCreating(false);
    }
  };

  const getAvatarLetter = (n) => (n || 'U')[0].toUpperCase();

  return (
    <>
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        Create From Existing
      </DialogTitle>

      <DialogContent dividers>
        {/* Version Name */}
        <TextField
          fullWidth
          label="Version Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          size="small"
          sx={{ mb: 3 }}
        />

        {/* Copy From */}
        <FormControl fullWidth size="small" sx={{ mb: 3 }}>
          <InputLabel>Copy From...</InputLabel>
          <Select
            value={sourceVersionId}
            onChange={handleSourceChange}
            label="Copy From..."
            renderValue={(selected) => {
              const v = versions.find(x => x.id === selected);
              if (!v) return <em>Choose a version</em>;
              return (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <VersionFrontThumbnail version={v} width={24} height={32} />
                  <span>{v.name}</span>
                </Box>
              );
            }}
          >
            {versions.length === 0 && (
              <MenuItem value="" disabled>
                <em>No versions to copy from</em>
              </MenuItem>
            )}
            {versions.map((v) => (
              <MenuItem key={v.id} value={v.id}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, width: '100%' }}>
                  <VersionFrontThumbnail version={v} />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>{v.name}</Typography>
                    {v.coverType && (
                      <Typography variant="caption" sx={{ color: '#fff' }}>
                        {v.coverType.replace(/_/g, ' ')}
                      </Typography>
                    )}
                  </Box>
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Cover Type */}
        <FormControl fullWidth size="small" sx={{ mb: 3 }}>
          <InputLabel>Cover Type (optional)</InputLabel>
          <Select
            value={coverType}
            onChange={(e) => { coverTypeTouched.current = true; setCoverType(e.target.value); }}
            label="Cover Type (optional)"
          >
            <MenuItem value="">
              <em>Not specified</em>
            </MenuItem>
            {COVER_TYPES.map((ct) => (
              <MenuItem key={ct.id} value={ct.id}>{ct.label}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <Divider sx={{ mb: 2 }} />

        {/* Design Criteria */}
        <Typography variant="subtitle2" sx={{ mb: 1, color: '#3B3478', fontWeight: 600 }}>
          📋 Design Criteria (optional)
        </Typography>
        <TextField
          fullWidth
          multiline
          rows={4}
          value={criteria}
          onChange={(e) => { criteriaTouched.current = true; setCriteria(e.target.value); }}
          inputProps={{ maxLength: 2000 }}
          helperText={`${criteria.length} / 2000`}
          sx={{ mb: 3 }}
        />

        <Divider sx={{ mb: 2 }} />

        {/* Collaborators */}
        <Typography variant="subtitle2" sx={{ mb: 1, color: '#3B3478', fontWeight: 600 }}>
          👥 Collaborators (optional)
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
          Add users who can help design or review this version
        </Typography>

        {collaborators.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            {collaborators.map((c) => (
              <Chip
                key={c.userId}
                avatar={<Avatar sx={{ bgcolor: c.role === 'design' ? '#8b5cf6' : '#0ea5e9' }}>
                  {getAvatarLetter(c.username)}
                </Avatar>}
                label={`${c.username} (${c.role === 'design' ? '🎨 Designer' : '👁️ Viewer'})`}
                onDelete={() => handleRemoveCollaborator(c.userId)}
                variant="outlined"
                sx={{
                  borderColor: c.role === 'design' ? '#c4b5fd' : '#7dd3fc',
                  backgroundColor: c.role === 'design' ? '#ede9fe' : '#e0f2fe',
                }}
              />
            ))}
          </Box>
        )}

        <Button
          variant="outlined"
          size="small"
          onClick={() => setAddOpen(true)}
          sx={{
            borderColor: '#5A52A3', color: '#5A52A3',
            borderRadius: '10px',
            textTransform: 'none', fontWeight: 600,
          }}
        >
          + Add Collaborators
        </Button>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={creating}>Cancel</Button>
        <Button
          onClick={handleCreate}
          variant="contained"
          disabled={creating}
          sx={{ backgroundColor: '#3B3478', '&:hover': { backgroundColor: '#2a2558' } }}
        >
          {creating ? 'Creating...' : 'Create Version'}
        </Button>
      </DialogActions>
    </Dialog>

    {/* Add Collaborators sub-dialog — same UX as the BookCover collaborator bar */}
    <Dialog open={addOpen} onClose={() => setAddOpen(false)} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6" fontWeight={600}>Add Collaborators</Typography>
        <IconButton size="small" onClick={() => setAddOpen(false)}><CloseIcon /></IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 1 }}>
        {/* Search bar */}
        <Box sx={{ position: 'relative', mb: 2 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search users by username..."
            value={addSearchQuery}
            onChange={(e) => setAddSearchQuery(e.target.value)}
            autoFocus
            InputProps={{
              endAdornment: addSearching ? <CircularProgress size={16} /> : null,
            }}
          />
          {(addSearchResults.length > 0 || (!addSearching && addSearchQuery.trim() && addSearchResults.length === 0)) && (
            <Box sx={{
              position: 'absolute', top: '100%', left: 0, right: 0, mt: 0.5,
              bgcolor: 'white', border: '1px solid #e0e0e0', borderRadius: 1,
              boxShadow: '0 4px 12px rgba(0,0,0,0.12)', zIndex: 20,
              maxHeight: 200, overflow: 'auto',
            }}>
              {addSearchResults.length > 0 ? addSearchResults.map((user) => {
                const selected = isPending(user);
                return (
                  <Box
                    key={user._id || user.id}
                    onClick={() => handleTogglePending(user)}
                    sx={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      p: 1, cursor: 'pointer',
                      backgroundColor: selected ? '#ede9fe' : 'transparent',
                      '&:hover': { bgcolor: selected ? '#ddd6fe' : '#f5f5f5' },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Avatar sx={{ width: 28, height: 28, fontSize: '0.875rem', bgcolor: '#5A52A3' }}>
                        {getAvatarLetter(user.username)}
                      </Avatar>
                      <Typography variant="body2">{user.username}</Typography>
                    </Box>
                    {selected && <CheckIcon sx={{ color: '#8b5cf6', fontSize: 18 }} />}
                  </Box>
                );
              }) : (
                <Box sx={{ p: 1.5 }}>
                  <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                    No users found
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </Box>

        {/* Selected users (chips) */}
        {addPendingUsers.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" sx={{ color: '#6b7280', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Selected ({addPendingUsers.length})
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 0.75 }}>
              {addPendingUsers.map((user) => (
                <Chip
                  key={user._id || user.id}
                  label={user.username}
                  size="small"
                  onDelete={() => handleTogglePending(user)}
                  avatar={<Avatar sx={{ bgcolor: '#5A52A3', fontSize: '0.7rem' }}>{getAvatarLetter(user.username)}</Avatar>}
                  sx={{ backgroundColor: '#ede9fe', color: '#5A52A3', borderColor: '#c4b5fd' }}
                />
              ))}
            </Box>
          </Box>
        )}

        {/* Role picker — applied to all selected users */}
        <Typography variant="body2" sx={{ color: '#374151', fontWeight: 600, mb: 1 }}>
          Assign role for this group:
        </Typography>
        <ToggleButtonGroup
          value={addRole}
          exclusive
          onChange={(_, val) => val && setAddRole(val)}
          fullWidth
          sx={{ mb: 1.5 }}
        >
          <ToggleButton
            value="design"
            sx={{
              display: 'flex', flexDirection: 'column', gap: 0.5, py: 1.5,
              '&.Mui-selected': { backgroundColor: '#ede9fe', borderColor: '#8b5cf6', color: '#8b5cf6' },
            }}
          >
            <BrushIcon />
            <Typography variant="body2" fontWeight={600}>Design</Typography>
            <Typography variant="caption" sx={{ fontSize: '0.65rem', textTransform: 'none' }}>
              Can edit & design the cover
            </Typography>
          </ToggleButton>
          <ToggleButton
            value="view_only"
            sx={{
              display: 'flex', flexDirection: 'column', gap: 0.5, py: 1.5,
              '&.Mui-selected': { backgroundColor: '#e0f2fe', borderColor: '#0ea5e9', color: '#0ea5e9' },
            }}
          >
            <VisibilityIcon />
            <Typography variant="body2" fontWeight={600}>View Only</Typography>
            <Typography variant="caption" sx={{ fontSize: '0.65rem', textTransform: 'none' }}>
              Can view & provide feedback
            </Typography>
          </ToggleButton>
        </ToggleButtonGroup>
      </DialogContent>

      <DialogActions>
        <Button onClick={() => setAddOpen(false)} sx={{ color: 'text.secondary' }}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleAddCommit}
          disabled={addPendingUsers.length === 0}
          sx={{ bgcolor: '#5A52A3', '&:hover': { bgcolor: '#3B3478' }, borderRadius: '8px', minWidth: 130 }}
        >
          Add {addPendingUsers.length > 0 ? `(${addPendingUsers.length})` : ''}
        </Button>
      </DialogActions>
    </Dialog>
    </>
  );
}
